import moment from 'moment-timezone'
import { QUIET_HOURS_END_HOUR, QUIET_HOURS_START_HOUR } from 'app/lib/smsQuietHours'

/**
 * The time core for scheduled SMS.
 *
 * A scheduled send stores a naive wall clock (`sendAtLocal`, e.g.
 * `2026-09-01T08:00`) rather than an instant, because people schedule "8:00 AM"
 * and not a moment in UTC. The wall clock resolves against the practice
 * location's IANA zone, which keeps a bulk run spanning timezones at 8:00 AM
 * for every patient and keeps a send correct across a daylight-saving change.
 *
 * Every function here takes the practice timezone as a required argument. There
 * is deliberately no viewer-clock fallback: a message that fires hours later
 * with nobody watching must not run on whichever clock the scheduling user
 * happened to be sitting in.
 */

/** The naive wall-clock format persisted as `sendAtLocal`. */
const WALL_CLOCK_FORMAT = `YYYY-MM-DDTHH:mm`

/** "Later today" lands this far ahead, rounded up to the next half hour. */
const LATER_TODAY_HOURS = 3

export type SchedulePreset = { id: string; label: string; sendAtLocal: string }

/** The absolute instant a wall-clock target resolves to at a given practice. */
export function resolveSendAt(sendAtLocal: string, timezone: string): Date {
  return moment.tz(sendAtLocal, WALL_CLOCK_FORMAT, timezone).toDate()
}

/**
 * Whether a wall-clock target can be scheduled at this practice: parseable,
 * still in the future on the practice clock, and outside quiet hours.
 */
export function isSchedulable(sendAtLocal: string, timezone: string, now: Date = new Date()): boolean {
  // moment-timezone does not error on an unrecognized zone name: it logs a
  // warning and silently resolves it as UTC, so `target.isValid()` below
  // would stay true. This must be checked explicitly, before any parsing,
  // or a bogus zone would compute schedulability against the wrong clock.
  if (!moment.tz.zone(timezone)) return false
  const target = moment.tz(sendAtLocal, WALL_CLOCK_FORMAT, timezone)
  if (!target.isValid()) return false
  if (!target.isAfter(moment(now))) return false
  const hour = target.hour()
  return hour >= QUIET_HOURS_END_HOUR && hour < QUIET_HOURS_START_HOUR
}

/** The soonest wall clock a send is allowed at this practice: the next 8:00 AM. */
export function nextSendableWallClock(timezone: string, now: Date = new Date()): string {
  const practice = moment.tz(now, timezone)
  // Before 8:00 AM the opening is later today; at or after it, tomorrow's.
  const morning = practice.clone().startOf(`day`).hour(QUIET_HOURS_END_HOUR)
  const target = practice.hour() < QUIET_HOURS_END_HOUR ? morning : morning.add(1, `day`)
  return target.format(WALL_CLOCK_FORMAT)
}

/**
 * The presets offered above the picker, filtered to the ones that are actually
 * valid right now. A preset that opens the picker onto a disabled time is a
 * small betrayal, so an invalid one is never rendered at all.
 */
export function schedulePresets(timezone: string, now: Date = new Date()): SchedulePreset[] {
  // Same reason as `isSchedulable`: an unrecognized zone resolves silently as
  // UTC, so every candidate below would be computed on the wrong clock and
  // "Tomorrow 8:00 AM" would land at 3:00 AM at an Eastern practice, inside
  // quiet hours. Offer nothing rather than times that are not what they say.
  if (!moment.tz.zone(timezone)) return []

  const practice = moment.tz(now, timezone)
  const candidates: SchedulePreset[] = []

  const later = practice.clone().add(LATER_TODAY_HOURS, `hours`)
  const remainder = later.minute() % 30
  if (remainder > 0) later.add(30 - remainder, `minutes`)
  later.second(0).millisecond(0)
  if (later.isSame(practice, `day`)) {
    candidates.push({ id: `later_today`, label: `Later today`, sendAtLocal: later.format(WALL_CLOCK_FORMAT) })
  }

  const tomorrow = practice.clone().add(1, `day`).startOf(`day`).hour(QUIET_HOURS_END_HOUR)
  candidates.push({
    id: `tomorrow_morning`,
    label: `Tomorrow 8:00 AM`,
    sendAtLocal: tomorrow.format(WALL_CLOCK_FORMAT),
  })

  // `isoWeekday(1)` is the Monday of the current ISO week, so roll forward a
  // week whenever that Monday is not still ahead of us.
  const monday = practice.clone().startOf(`day`).isoWeekday(1).hour(9)
  if (!monday.isAfter(practice)) monday.add(1, `week`)
  if (!monday.isSame(tomorrow, `day`)) {
    candidates.push({
      id: `monday_morning`,
      label: `Monday 9:00 AM`,
      sendAtLocal: monday.format(WALL_CLOCK_FORMAT),
    })
  }

  // Every candidate goes through the gate the custom picker applies, not just
  // "Later today". A preset the picker would refuse must never be offered.
  return candidates.filter((preset) => isSchedulable(preset.sendAtLocal, timezone, now))
}

/**
 * How a scheduled time reads to the user, e.g. "Tomorrow 8:00 AM" or
 * "Thu 9:00 AM MDT". The practice zone is appended only when its offset differs
 * from the viewer's, matching the `practiceLocalTimeLabel` convention: someone
 * sitting in the same zone as the practice does not need to be told which zone
 * they are in.
 */
export function sendAtLabel(sendAtLocal: string, timezone: string, now: Date = new Date()): string {
  const target = moment.tz(sendAtLocal, WALL_CLOCK_FORMAT, timezone)
  if (!target.isValid()) return ``
  const practice = moment.tz(now, timezone)
  const days = target.clone().startOf(`day`).diff(practice.clone().startOf(`day`), `days`)

  let day: string
  if (days === 0) day = `Today`
  else if (days === 1) day = `Tomorrow`
  else if (days > 1 && days < 7) day = target.format(`ddd`)
  else day = target.format(`MMM D,`)

  const zone = target.utcOffset() === moment(now).utcOffset() ? `` : ` ${target.format(`z`)}`
  return `${day} ${target.format(`h:mm A`)}${zone}`
}
